import { Suspense, lazy, type ReactElement } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { LandingPage } from './components/Landing/LandingPage';
import { CoachLandingPage } from './components/Landing/CoachLandingPage';
import { LegalPages } from './components/Legal/LegalPages';
import { PublicBookingPage } from './components/Schedule/PublicBookingPage';
import { GuestBookingManagePage } from './components/Schedule/GuestBookingManagePage';
const LazyPublicStartPage = lazy(async () => ({ default: (await import('./components/PublicStart/PublicStartPage')).PublicStartPage }));
const LazyAcceptCoachInvitePage = lazy(async () => ({ default: (await import('./components/Trainer/AcceptCoachInvitePage')).AcceptCoachInvitePage }));

const LazyLoginPage = lazy(async () => ({ default: (await import('./components/Auth/LoginPage')).LoginPage }));
const LazyRequireAuth = lazy(async () => ({ default: (await import('./app/auth')).RequireAuth }));
const LazyAppHomeRedirect = lazy(async () => ({ default: (await import('./app/shells')).AppHomeRedirect }));
const LazyClientMoreLanding = lazy(async () => ({ default: (await import('./app/shells')).ClientMoreLanding }));
const LazyClientMoreLayout = lazy(async () => ({ default: (await import('./app/shells')).ClientMoreLayout }));
const LazyClientShell = lazy(async () => ({ default: (await import('./app/shells')).ClientShell }));
const LazyCoachMoreLanding = lazy(async () => ({ default: (await import('./app/shells')).CoachMoreLanding }));
const LazyCoachMoreLayout = lazy(async () => ({ default: (await import('./app/shells')).CoachMoreLayout }));
const LazyCoachShell = lazy(async () => ({ default: (await import('./app/shells')).CoachShell }));
const LazyClientBillingPage = lazy(async () => ({ default: (await import('./app/pages')).ClientBillingPage }));
const LazyClientChatPage = lazy(async () => ({ default: (await import('./app/pages')).ClientChatPage }));
const LazyClientFilesPage = lazy(async () => ({ default: (await import('./app/pages')).ClientFilesPage }));
const LazyClientInsightsPage = lazy(async () => ({ default: (await import('./app/pages')).ClientInsightsPage }));
const LazyClientNutritionPage = lazy(async () => ({ default: (await import('./app/pages')).ClientNutritionPage }));
const LazyClientMyPlanPage = lazy(async () => ({ default: (await import('./app/pages')).ClientMyPlanPage }));
const LazyClientPlanPage = lazy(async () => ({ default: (await import('./app/pages')).ClientPlanPage }));
const LazyClientSchedulePage = lazy(async () => ({ default: (await import('./app/pages')).ClientSchedulePage }));
const LazyClientSettingsPage = lazy(async () => ({ default: (await import('./app/pages')).ClientSettingsPage }));
const LazyClientWorkoutsPage = lazy(async () => ({ default: (await import('./app/pages')).ClientWorkoutsPage }));
const LazyCoachBillingPage = lazy(async () => ({ default: (await import('./app/pages')).CoachBillingPage }));
const LazyCoachClientDetailPage = lazy(async () => ({ default: (await import('./app/pages')).CoachClientDetailPage }));
const LazyCoachReviewsPage = lazy(async () => ({ default: (await import('./app/pages')).CoachReviewsPage }));
const LazyCoachRosterPage = lazy(async () => ({ default: (await import('./app/pages')).CoachRosterPage }));
const LazyCoachSchedulePage = lazy(async () => ({ default: (await import('./app/pages')).CoachSchedulePage }));
const LazyCoachSettingsPage = lazy(async () => ({ default: (await import('./app/pages')).CoachSettingsPage }));
const LazyCoachTrainingClientPage = lazy(async () => ({ default: (await import('./app/pages')).CoachTrainingClientPage }));
const LazyCoachTrainingClientSessionPage = lazy(async () => ({ default: (await import('./app/pages')).CoachTrainingClientSessionPage }));
const LazyAdminCoachApplicationsPage = lazy(async () => ({ default: (await import('./app/pages')).AdminCoachApplicationsPage }));

function LegalPageRoute() {
  const navigate = useNavigate();
  return <LegalPages onBack={() => navigate(-1)} />;
}

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-app-bg)] px-6">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[rgba(113,97,79,0.14)] border-t-[var(--color-brand)]" />
    </div>
  );
}

function withSuspense(element: ReactElement) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/coaches" element={<CoachLandingPage />} />
      <Route path="/start" element={withSuspense(<LazyPublicStartPage />)} />
      <Route path="/accept-coach-invite" element={withSuspense(<LazyAcceptCoachInvitePage />)} />
      <Route path="/login" element={withSuspense(<LazyLoginPage />)} />
      <Route path="/legal" element={<LegalPageRoute />} />
      <Route path="/book/:publicSlug" element={<PublicBookingPage />} />
      <Route path="/book/manage/:token" element={<GuestBookingManagePage />} />

      <Route path="/app" element={withSuspense(<LazyRequireAuth />)}>
        <Route index element={withSuspense(<LazyAppHomeRedirect />)} />

        {/* Admin-only (no nav link; server 403-gates, page redirects non-admins) — MP 5.9 */}
        <Route path="admin/coach-applications" element={withSuspense(<LazyAdminCoachApplicationsPage />)} />

        <Route path="chat" element={<Navigate to="/app/client/chat" replace />} />
        <Route path="files" element={<Navigate to="/app/client/more/files" replace />} />
        <Route path="dashboard" element={<Navigate to="/app/client/insights" replace />} />
        <Route path="schedule" element={<Navigate to="/app/client/more/schedule" replace />} />
        <Route path="billing" element={<Navigate to="/app/client/more/billing" replace />} />
        <Route path="settings" element={<Navigate to="/app/client/more/settings" replace />} />
        <Route path="legal" element={<Navigate to="/legal" replace />} />

        <Route path="client" element={withSuspense(<LazyClientShell />)}>
          <Route index element={<Navigate to="chat" replace />} />
          <Route path="chat" element={withSuspense(<LazyClientChatPage />)} />
          <Route path="workouts" element={withSuspense(<LazyClientWorkoutsPage />)} />
          <Route path="nutrition" element={withSuspense(<LazyClientNutritionPage />)} />
          <Route path="plan" element={withSuspense(<LazyClientMyPlanPage />)} />
          <Route path="plan/week" element={withSuspense(<LazyClientPlanPage />)} />
          <Route path="insights" element={withSuspense(<LazyClientInsightsPage />)} />
          <Route path="more" element={withSuspense(<LazyClientMoreLayout />)}>
            <Route index element={withSuspense(<LazyClientMoreLanding />)} />
            <Route path="settings" element={withSuspense(<LazyClientSettingsPage />)} />
            <Route path="schedule" element={withSuspense(<LazyClientSchedulePage />)} />
            <Route path="billing" element={withSuspense(<LazyClientBillingPage />)} />
            <Route path="files" element={withSuspense(<LazyClientFilesPage />)} />
          </Route>
        </Route>

        <Route path="coach" element={withSuspense(<LazyCoachShell />)}>
          <Route index element={<Navigate to="roster" replace />} />
          <Route path="roster" element={withSuspense(<LazyCoachRosterPage />)} />
          <Route path="clients/:clientId" element={withSuspense(<LazyCoachClientDetailPage />)} />
          <Route path="training-clients/:clientUserId" element={withSuspense(<LazyCoachTrainingClientPage />)} />
          <Route path="training-clients/:clientUserId/session" element={withSuspense(<LazyCoachTrainingClientSessionPage />)} />
          <Route path="reviews" element={withSuspense(<LazyCoachReviewsPage />)} />
          <Route path="schedule" element={withSuspense(<LazyCoachSchedulePage />)} />
          <Route path="more" element={withSuspense(<LazyCoachMoreLayout />)}>
            <Route index element={withSuspense(<LazyCoachMoreLanding />)} />
            <Route path="settings" element={withSuspense(<LazyCoachSettingsPage />)} />
            <Route path="billing" element={withSuspense(<LazyCoachBillingPage />)} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
